/*!
 * TimbricaPianoSynth — Steinway-grade additive piano synthesizer
 * Extracted from endless-piano (resources/views/tools/endless-piano.blade.php).
 * Interactive-friendly: noteOn/noteOff API with sustain-pedal semantics + voice variants.
 *
 * Usage:
 *   var synth = TimbricaPianoSynth.create({ voice: 'grand', reverb: 'warm' });
 *   synth.init();           // creates AudioContext on first user gesture
 *   synth.noteOn(60, 0.8);  // middle C, velocity 0..1
 *   synth.noteOff(60);
 *   synth.setVoice('electric');
 *   synth.setSustain(true); // hold notes until pedal released
 *   synth.getAnalyser();    // AnalyserNode for visualization
 *   synth.getMediaStream(); // MediaStream for MediaRecorder
 *
 * Voices: grand · electric · pad · organ · harpsichord
 * Reverbs: dry · warm · cathedral
 *
 * ES5-safe (no ?., ??, ??=, ||=, &&=). No build step required.
 */
window.TimbricaPianoSynth = (function () {
    'use strict';

    /* Steinway-like harmonic amplitudes (16 partials) */
    var HARM_AMPS = [1.0, 0.50, 0.35, 0.25, 0.18, 0.14, 0.10, 0.08, 0.06, 0.05, 0.04, 0.032, 0.025, 0.020, 0.016, 0.012];

    /* StereoPanner is missing in Safari < 14.1 (iOS 12/13). Fall back to a plain
       GainNode: it passes audio through unchanged (mono, no stereo spread), so the
       whole graph still connects and the piano works on legacy WebKit instead of
       throwing "createStereoPanner is not a function" and ABORTING init — which on
       iOS 12 then retried until the 4-context limit handed back a broken object and
       .state threw the "webkitAudioContext.state getter" error (TIMBRICA-FRONTEND-
       AR is the root; AT/AS were the retry cascade). organ-synth.js already guards
       the identical call. */
    function _panNode(ctx, v) {
        if (ctx.createStereoPanner) {
            var p = ctx.createStereoPanner();
            p.pan.value = v;
            return p;
        }
        return ctx.createGain();
    }

    /* Inharmonicity coefficient — bass strings more rigid than treble */
    function getB(midi) {
        var n = (midi - 21) / 87;
        if (n < 0) n = 0; if (n > 1) n = 1;
        return 0.0002 + 0.012 * Math.pow(n, 2.5);
    }

    function getBaseDecay(midi) {
        var n = (midi - 21) / 87;
        return 1.2 + 10 * Math.pow(1 - n, 1.8);
    }

    /* Register-dependent envelope. Voice may override sus/rel. */
    function getEnvelope(midi, velocity, voice) {
        var n = (midi - 36) / 48;
        if (n < 0) n = 0; if (n > 1) n = 1;
        var env = {
            atk: 0.003 + 0.013 * (1 - n),
            dec: 0.10 + 0.22 * (1 - n),
            sus: 0.15 + 0.25 * velocity,
            rel: 0.8 + 2.4 * (1 - n)
        };
        if (voice === 'pad') {
            env.atk = 0.06 + 0.05 * (1 - n);
            env.dec = 0.4;
            env.sus = 0.85 + 0.10 * velocity;
            env.rel = 1.6 + 1.2 * (1 - n);
        } else if (voice === 'organ') {
            env.atk = 0.005;
            env.dec = 0.0;
            env.sus = 1.0;
            env.rel = 0.10;
        } else if (voice === 'electric') {
            env.dec = 0.15 + 0.30 * (1 - n);
            env.sus = 0.20 + 0.30 * velocity;
            env.rel = 0.6 + 1.8 * (1 - n);
        } else if (voice === 'harpsichord') {
            env.atk = 0.001;
            env.dec = 0.05;
            env.sus = 0.0;
            env.rel = 0.4 + 1.0 * (1 - n);
        }
        return env;
    }

    /* Per-voice partial amplitude scaling. Returns array same length as HARM_AMPS. */
    function getPartialAmps(voice) {
        if (voice === 'electric') {
            /* DX7/Rhodes-ish: mostly fundamental + sparse upper partials */
            return [1.0, 0.30, 0.18, 0.10, 0.08, 0.05, 0.04, 0.03, 0, 0, 0, 0, 0, 0, 0, 0];
        }
        if (voice === 'pad') {
            /* Smooth, fewer partials, gentle taper */
            return [1.0, 0.55, 0.30, 0.18, 0.10, 0.06, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
        }
        if (voice === 'organ') {
            /* Hammond-like drawbars at harmonics 1, 2, 3, 4, 6, 8 */
            return [1.0, 0.80, 0.60, 0.45, 0, 0.30, 0, 0.20, 0, 0, 0, 0, 0, 0, 0, 0];
        }
        /* harpsichord goes through a separate Karplus-Strong branch, not partials */
        return HARM_AMPS;
    }

    function midiToFreq(midi) {
        return 440 * Math.pow(2, (midi - 69) / 12);
    }

    /* ───────────────────────────────────────────────────────────── */

    function create(opts) {
        opts = opts || {};
        var inst = {};
        inst.voice = opts.voice || 'grand';
        inst.ctx = null;
        inst.master = null;
        inst.mixBus = null;
        inst.dryGain = null;
        inst.reverbGain = null;
        inst.convolver = null;
        inst.analyser = null;
        inst.mediaStreamDest = null;
        inst.hammerBuf = null;
        inst.activeNotes = {};      /* midi → { handle objects, releaseAt, isReleasing } */
        inst.sustainOn = false;
        inst.pendingReleases = {};  /* midi → true while sustained */
        inst.initialized = false;

        /* ───────────────── Init ───────────────── */
        inst.init = function () {
            if (inst.initialized) return;
            var ctx;
            if (opts.ctx) {
                /* External context injection — endless-piano export builds the
                   whole instrument inside an OfflineAudioContext. No resume():
                   offline contexts reject it before startRendering(). */
                ctx = inst.ctx = opts.ctx;
            } else {
                var Ctor = window.AudioContext || window.webkitAudioContext;
                if (!Ctor) return;
                ctx = inst.ctx = new Ctor();
                if (ctx.state === 'suspended') ctx.resume();
            }

            /* master = SYNTH input pre-gain. Overall volume moved to `level`
               (post-mix) so that real piano samples can join the chain AFTER
               the body EQ + chorus: those stages emulate a piano body/triple
               strings for the SYNTHESIZED voices — running actual Salamander
               recordings through the chorus added a slow audible pitch-wobble
               ("slight corruption", user report 2026-07-17). Gain math is
               unchanged: 1.0 (master) × 0.6 (level) = the old 0.6 master. */
            inst.master = ctx.createGain();
            inst.master.gain.value = 1.0;

            /* Soundboard resonance (body) */
            var body1 = ctx.createBiquadFilter();
            body1.type = 'peaking'; body1.frequency.value = 220; body1.Q.value = 1.2; body1.gain.value = 2.5;
            var body2 = ctx.createBiquadFilter();
            body2.type = 'peaking'; body2.frequency.value = 2600; body2.Q.value = 0.8; body2.gain.value = 1.8;
            var warmth = ctx.createBiquadFilter();
            warmth.type = 'lowshelf'; warmth.frequency.value = 300; warmth.gain.value = 1.5;

            /* Stereo chorus (triple-string detune simulation) */
            var chorusL = ctx.createDelay(0.1); chorusL.delayTime.value = 0.004;
            var chorusR = ctx.createDelay(0.1); chorusR.delayTime.value = 0.007;
            var lfo1 = ctx.createOscillator(); lfo1.frequency.value = 0.22;
            var lg1 = ctx.createGain(); lg1.gain.value = 0.0008;
            lfo1.connect(lg1); lg1.connect(chorusL.delayTime); lfo1.start();
            var lfo2 = ctx.createOscillator(); lfo2.frequency.value = 0.31;
            var lg2 = ctx.createGain(); lg2.gain.value = 0.0006;
            lfo2.connect(lg2); lg2.connect(chorusR.delayTime); lfo2.start();
            var cGL = ctx.createGain(); cGL.gain.value = 0.16;
            var cGR = ctx.createGain(); cGR.gain.value = 0.16;
            var cPanL = _panNode(ctx, -0.25);
            var cPanR = _panNode(ctx, 0.25);

            inst.mixBus = ctx.createGain(); inst.mixBus.gain.value = 1.0;

            /* Reverb path */
            inst.convolver = ctx.createConvolver();
            inst.dryGain = ctx.createGain(); inst.dryGain.gain.value = 0.65;
            inst.reverbGain = ctx.createGain(); inst.reverbGain.gain.value = 0.35;
            /* The analyser sits IN the output path, so it is always made; its
               size is only a wish: an anti-fingerprinting extension (Android
               Yandex/Chromium builds) leaves AnalyserNode.fftSize getter-only,
               and the strict assignment threw - no piano at all (2 sessions in
               October 2026, the same visitors as /electric-guitar). */
            inst.analyser = ctx.createAnalyser();
            try { inst.analyser.fftSize = 256; } catch (e) {}

            /* master → body EQ → warmth → [dry + chorusL + chorusR] → mixBus */
            inst.master.connect(body1); body1.connect(body2); body2.connect(warmth);
            warmth.connect(inst.mixBus);
            warmth.connect(chorusL); chorusL.connect(cGL); cGL.connect(cPanL); cPanL.connect(inst.mixBus);
            warmth.connect(chorusR); chorusR.connect(cGR); cGR.connect(cPanR); cPanR.connect(inst.mixBus);

            /* level = the volume node (setVolume / sleep-fade target); both
               the synth mix and the direct sample feed pass through it, so
               volume and fades keep acting on everything at one point */
            inst.level = ctx.createGain();
            inst.level.gain.value = 0.6;
            /* clean entry for REAL samples: skips body EQ + chorus, keeps
               volume, fades and the shared reverb room. 1.2 = loudness makeup
               for the dropped EQ/chorus energy (measured −1.6 dB on the A4
               sample; +1.6 dB restores the previous level) */
            inst.sampleIn = ctx.createGain();
            inst.sampleIn.gain.value = 1.2;

            /* mixBus/sampleIn → level → dry/wet → analyser → destination */
            inst.mixBus.connect(inst.level);
            inst.sampleIn.connect(inst.level);
            inst.level.connect(inst.dryGain);
            inst.level.connect(inst.convolver);
            inst.convolver.connect(inst.reverbGain);
            inst.dryGain.connect(inst.analyser);
            inst.reverbGain.connect(inst.analyser);
            inst.analyser.connect(ctx.destination);

            inst.hammerBuf = _genHammer(ctx);
            inst.setReverb(opts.reverb || 'warm');
            inst.initialized = true;
        };

        /* ───────────────── Reverb IR generation ───────────────── */
        function genIR(dur, decay) {
            var ctx = inst.ctx;
            var sr = ctx.sampleRate, len = Math.floor(sr * dur);
            var buf = ctx.createBuffer(2, len, sr);
            var delays = [0.011, 0.023, 0.034, 0.047, 0.062, 0.079, 0.098];
            var gains = [0.72, 0.58, 0.45, 0.36, 0.29, 0.23, 0.18];
            for (var c = 0; c < 2; c++) {
                var d = buf.getChannelData(c);
                for (var r = 0; r < delays.length; r++) {
                    var idx = Math.floor(delays[r] * sr * (1 + c * 0.08));
                    if (idx < len) {
                        d[idx] += (c === 0 ? 1 : -1) * (Math.random() > 0.5 ? 1 : -1) * gains[r] * 0.28;
                        for (var s = 1; s < Math.floor(sr * 0.004); s++) {
                            if (idx + s < len) d[idx + s] += (Math.random() * 2 - 1) * gains[r] * 0.12 * Math.exp(-s / (sr * 0.0012));
                        }
                    }
                }
                var late = Math.floor(0.08 * sr);
                var prev1 = 0, prev2 = 0;
                for (var i = late; i < len; i++) {
                    var env = Math.pow(1 - (i - late) / (len - late), decay);
                    var noise = Math.random() * 2 - 1;
                    noise = noise * 0.35 + prev1 * 0.35 + prev2 * 0.2;
                    prev2 = prev1; prev1 = noise;
                    d[i] += noise * env * 0.22;
                }
            }
            return buf;
        }

        inst.setReverb = function (type) {
            if (!inst.ctx) return;
            if (type === 'dry') {
                inst.dryGain.gain.value = 1.0;
                inst.reverbGain.gain.value = 0;
            } else if (type === 'warm') {
                try { inst.convolver.buffer = genIR(2.2, 2.6); } catch (e) {}
                inst.dryGain.gain.value = 0.60;
                inst.reverbGain.gain.value = 0.40;
            } else {
                try { inst.convolver.buffer = genIR(5.0, 1.5); } catch (e) {}
                inst.dryGain.gain.value = 0.35;
                inst.reverbGain.gain.value = 0.65;
            }
        };

        /* ───────────────── Note on (interactive — no end time) ───────────────── */
        inst.noteOn = function (midi, velocity, atTime) {
            inst.init();
            if (!inst.ctx) return null;
            if (typeof velocity !== 'number' || velocity < 0.01) velocity = 0.6;
            if (velocity > 1) velocity = 1;
            var when = (typeof atTime === 'number') ? atTime : inst.ctx.currentTime;

            /* If already held, release the old one first (re-trigger) */
            if (inst.activeNotes[midi]) {
                _release(midi, when);
            }

            if (inst.voice === 'harpsichord') {
                var hHandle = _karplusOn(midi, velocity, when);
                inst.activeNotes[midi] = hHandle;
                return hHandle;
            }

            var handle = _additiveOn(midi, velocity, when);
            inst.activeNotes[midi] = handle;
            return handle;
        };

        /* ───────────────── Note off (with sustain) ───────────────── */
        inst.noteOff = function (midi, atTime) {
            if (!inst.ctx || !inst.activeNotes[midi]) return;
            var when = (typeof atTime === 'number') ? atTime : inst.ctx.currentTime;
            if (inst.sustainOn) {
                inst.pendingReleases[midi] = true;
                return;
            }
            _release(midi, when);
        };

        function _release(midi, atTime) {
            var handle = inst.activeNotes[midi];
            if (!handle || handle.releasing) return;
            handle.releasing = true;
            var env = getEnvelope(midi, handle.velocity, inst.voice);
            var rel = env.rel;
            var ctx = inst.ctx;
            try {
                /* Cancel pending ramps, then exponential ramp to silence */
                handle.noteGain.gain.cancelScheduledValues(atTime);
                handle.noteGain.gain.setValueAtTime(handle.noteGain.gain.value, atTime);
                handle.noteGain.gain.exponentialRampToValueAtTime(0.0001, atTime + rel);
                /* Schedule stop of oscillators */
                for (var i = 0; i < handle.oscs.length; i++) {
                    try { handle.oscs[i].stop(atTime + rel + 0.05); } catch (e) {}
                }
                if (handle.src) {
                    try { handle.src.stop(atTime + rel + 0.05); } catch (e) {}
                }
            } catch (e) {}
            delete inst.activeNotes[midi];
            /* Cleanup nodes once release finishes */
            var cleanupMs = (rel + 0.2) * 1000;
            setTimeout(function () {
                try {
                    if (handle.oscs) for (var i = 0; i < handle.oscs.length; i++) handle.oscs[i].disconnect();
                    if (handle.hGs) for (var j = 0; j < handle.hGs.length; j++) handle.hGs[j].disconnect();
                    if (handle.filter) handle.filter.disconnect();
                    if (handle.panner) handle.panner.disconnect();
                    if (handle.noteGain) handle.noteGain.disconnect();
                    if (handle.src) handle.src.disconnect();
                    if (handle.env) handle.env.disconnect();
                } catch (e) {}
            }, cleanupMs);
        }

        /* ───────────────── Sustain pedal ───────────────── */
        inst.setSustain = function (on) {
            inst.sustainOn = !!on;
            if (!on) {
                /* Release all pending */
                var now = inst.ctx ? inst.ctx.currentTime : 0;
                for (var midi in inst.pendingReleases) {
                    if (Object.prototype.hasOwnProperty.call(inst.pendingReleases, midi)) {
                        _release(parseInt(midi, 10), now);
                    }
                }
                inst.pendingReleases = {};
            }
        };

        /* ───────────────── Voice switch ───────────────── */
        inst.setVoice = function (name) {
            if (name === inst.voice) return;
            /* Hard-stop all sounding notes — prevents stuck notes on voice change */
            inst.stopAll(true);
            inst.voice = name;
        };

        inst.setVolume = function (v) {
            if (!inst.master) return;
            if (v < 0) v = 0; if (v > 1) v = 1;
            var vol = inst.level || inst.master;
            vol.gain.setTargetAtTime(v, inst.ctx.currentTime, 0.02);
        };

        inst.stopAll = function (immediate) {
            if (!inst.ctx) return;
            var now = inst.ctx.currentTime;
            for (var midi in inst.activeNotes) {
                if (!Object.prototype.hasOwnProperty.call(inst.activeNotes, midi)) continue;
                if (immediate) {
                    var h = inst.activeNotes[midi];
                    try {
                        h.noteGain.gain.cancelScheduledValues(now);
                        h.noteGain.gain.setValueAtTime(h.noteGain.gain.value, now);
                        h.noteGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.05);
                        for (var i = 0; i < (h.oscs || []).length; i++) try { h.oscs[i].stop(now + 0.06); } catch (e) {}
                        if (h.src) try { h.src.stop(now + 0.06); } catch (e) {}
                    } catch (e) {}
                } else {
                    _release(parseInt(midi, 10), now);
                }
            }
            inst.activeNotes = {};
            inst.pendingReleases = {};
        };

        inst.getAnalyser = function () { return inst.analyser; };

        /* The volume node: setVolume targets it and the endless-piano sleep
           fade ramps its gain — everything (synth + samples) passes through. */
        inst.getInputBus = function () { return inst.level || inst.master; };

        /* Clean entry for REAL sample playback (endless-piano sampler):
           post-EQ, post-chorus — obeys setVolume/fades and shares the reverb
           room, without the synth-only body/chorus coloring. */
        inst.getSampleBus = function () { return inst.sampleIn || inst.master; };

        inst.getMediaStream = function () {
            if (!inst.ctx) return null;
            if (!inst.mediaStreamDest) {
                inst.mediaStreamDest = inst.ctx.createMediaStreamDestination();
                /* Tap from level (post-mix, incl. direct sample feed) so the
                   recorded signal matches what the user hears */
                try { (inst.level || inst.mixBus).connect(inst.mediaStreamDest); } catch (e) {}
            }
            return inst.mediaStreamDest.stream;
        };

        /* ───────────────── Additive voice (grand / electric / pad / organ) ───────────────── */
        function _additiveOn(midi, velocity, startTime) {
            var ctx = inst.ctx;
            var freq = midiToFreq(midi);
            var B = getB(midi);
            var env = getEnvelope(midi, velocity, inst.voice);
            var velBright = Math.pow(velocity, 0.7);
            var partialAmps = getPartialAmps(inst.voice);

            var noteGain = ctx.createGain();
            var filter = ctx.createBiquadFilter();
            filter.type = 'lowpass';
            filter.frequency.value = 1200 + velocity * 5800;
            filter.Q.value = 0.5 + velocity * 0.3;

            var panner = _panNode(ctx, Math.max(-0.6, Math.min(0.6, (midi - 60) / 38)));

            /* Hammer transient — grand and electric only */
            if (inst.hammerBuf && (inst.voice === 'grand' || inst.voice === 'electric')) {
                var ham = ctx.createBufferSource(); ham.buffer = inst.hammerBuf;
                var hG = ctx.createGain();
                var hF = ctx.createBiquadFilter();
                hF.type = 'bandpass';
                var hammerCenter = (inst.voice === 'electric') ? (3000 + velocity * 6000 + midi * 20) : (2000 + velocity * 4000 + midi * 30);
                hF.frequency.value = hammerCenter;
                hF.Q.value = 0.8 + velocity * 0.6;
                var hVol = (inst.voice === 'electric') ? velocity * 0.18 : velocity * 0.28;
                hG.gain.setValueAtTime(hVol, startTime);
                hG.gain.exponentialRampToValueAtTime(0.0001, startTime + 0.045);
                ham.connect(hF); hF.connect(hG); hG.connect(panner);
                ham.start(startTime); ham.stop(startTime + 0.06);
            }

            /* Additive partials */
            var numH = Math.min(16, Math.floor(ctx.sampleRate / 2 / freq));
            var oscs = [], hGs = [];
            var inharmMul = (inst.voice === 'electric') ? 3 : 1;
            for (var h = 0; h < numH; h++) {
                var n = h + 1;
                var hFreq = freq * n * Math.sqrt(1 + B * inharmMul * n * n);
                if (hFreq > ctx.sampleRate / 2) break;
                var baseAmp = partialAmps[h] || 0;
                if (baseAmp <= 0) continue;

                var osc = ctx.createOscillator();
                var hGain = ctx.createGain();
                osc.type = 'sine';
                osc.frequency.value = hFreq;
                /* Mild per-partial detuning (string imperfection) */
                osc.detune.value = (Math.random() - 0.5) * 1.2 * n * 0.15;

                var brightMul = (h > 3) ? (0.25 + velBright * 0.75) : 1.0;
                var amp = baseAmp * brightMul * velocity * 0.08;

                /* Build ADSR up to sustain — release deferred to noteOff */
                hGain.gain.setValueAtTime(0, startTime);
                hGain.gain.linearRampToValueAtTime(amp, startTime + env.atk);
                if (env.dec > 0) {
                    hGain.gain.exponentialRampToValueAtTime(
                        Math.max(amp * env.sus, 0.00005), startTime + env.atk + env.dec);
                } else {
                    /* Organ: no decay, hold sustain */
                    hGain.gain.setValueAtTime(amp * env.sus, startTime + env.atk);
                }

                osc.connect(hGain); hGain.connect(filter);
                osc.start(startTime);
                oscs.push(osc); hGs.push(hGain);
            }

            filter.connect(noteGain); noteGain.connect(panner); panner.connect(inst.master);
            noteGain.gain.setValueAtTime(0.85, startTime);

            return {
                voice: inst.voice,
                midi: midi,
                velocity: velocity,
                start: startTime,
                releasing: false,
                noteGain: noteGain,
                filter: filter,
                panner: panner,
                oscs: oscs,
                hGs: hGs
            };
        }

        /* ───────────────── Harpsichord — Karplus-Strong (lifted from acoustic-guitar) ───────────────── */
        function _karplusOn(midi, velocity, startTime) {
            var ctx = inst.ctx;
            var freq = midiToFreq(midi);
            if (freq < 20) return null;

            var sr = ctx.sampleRate;
            var period = Math.round(sr / freq);
            var durSamples = Math.round(sr * 4);
            var buf = ctx.createBuffer(1, durSamples, sr);
            var data = buf.getChannelData(0);
            var line = new Float32Array(period);
            var i;
            for (i = 0; i < period; i++) line[i] = (Math.random() * 2 - 1) * velocity;

            /* Brighter, slightly faster decay than guitar */
            var brightness = 0.6 + velocity * 0.25;
            var decay = 0.994 + velocity * 0.003;
            var pos = 0;
            for (i = 0; i < durSamples; i++) {
                var next = (pos + 1) % period;
                data[i] = line[pos];
                line[pos] = decay * (line[pos] * brightness + line[next] * (1 - brightness));
                pos = next;
            }

            var src = ctx.createBufferSource(); src.buffer = buf;
            var env = ctx.createGain();
            var panner = _panNode(ctx, Math.max(-0.5, Math.min(0.5, (midi - 60) / 40)));

            env.gain.setValueAtTime(0, startTime);
            env.gain.linearRampToValueAtTime(0.7 * velocity, startTime + 0.002);
            env.gain.exponentialRampToValueAtTime(0.0001, startTime + 4);
            src.connect(env); env.connect(panner); panner.connect(inst.master);
            src.start(startTime);

            return {
                voice: 'harpsichord',
                midi: midi,
                velocity: velocity,
                start: startTime,
                releasing: false,
                src: src,
                env: env,
                panner: panner,
                noteGain: env  /* alias so _release works generically */
            };
        }

        return inst;
    }

    /* Static helper: noise burst for hammer transient */
    function _genHammer(ctx) {
        var sr = ctx.sampleRate, len = Math.floor(sr * 0.06);
        var buf = ctx.createBuffer(1, len, sr);
        var d = buf.getChannelData(0);
        for (var i = 0; i < len; i++) {
            var t = i / sr;
            var env = Math.min(t / 0.002, 1) * Math.exp(-t / 0.015);
            d[i] = (Math.random() * 2 - 1) * env;
        }
        return buf;
    }

    return {
        create: create,
        midiToFreq: midiToFreq,
        VOICES: ['grand', 'electric', 'pad', 'organ', 'harpsichord']
    };
})();
